const camelCase = require('lodash/camelCase');
const dataDictionaryService = require('./index');

/**
 * Generates a Draft 2020-12 JSON Schema document from an hNode tree. The
 * resulting schema feeds OpenAPI generation and route access calculations, so
 * it needs to be deterministic, deduplicated, and aware of access/feature-flag
 * restrictions carried by extract().
 * @param {object} hNode
 * @param {{context?:object, id?:string, title?:string}} [options={}]
 * @returns {object}
 */
function buildSchema(hNode, options = {}) {
    const ctx = options.context || {};

    // the partialTypeDefinition is not used for building the OpenAPI schema
    // it matters for other callers (e.g. form block generators and createDataModel.fromHNodes) that build UI metadata
    // eslint-disable-next-line no-unused-vars
    const [subDataDictionary, partialTypeDefinition] = dataDictionaryService.extract(hNode, ctx);

    const root = createRootSchema(options);

    // JsonSchemaRegistry is auto-activated by extract() for deduplication
    const namespaces = buildNamespaceSchemas(subDataDictionary, ctx.jsonSchemaRegistry);
    Object.assign(root.properties, namespaces);
    Object.assign(root.$defs, ctx.jsonSchemaRegistry.toDefs());

    // Handle upstream merge issues that can cause invalid schema structure
    fixArrayObjectKeywordPlacement(root);

    // Dedupe inline objects that weren't registered by block emitters
    const { dedupeNode } = createSchemaDeduper(root);
    Object.entries(root.properties).forEach(([nsName, nsSchema]) => dedupeNode(nsSchema, [nsName]));
    Object.entries(root.$defs).forEach(([key, schema]) => dedupeNode(schema, [key]));

    // Remove default values from properties with dotted/bracketed names (e.g., "inventory.0.found").
    // These are nested path references used for UI display, not actual root-level properties.
    // Keeping their defaults causes AJV to incorrectly inject them into validated payloads.
    stripDefaultsFromNestedPathProperties(root);

    return root;
}

/**
 * Establishes the root container for the JSON Schema document, applying any
 * caller-supplied identifier or title while defaulting to Backbone conventions.
 * @param {{
 *     id?:string,
 *     title?:string
 * }} options
 * @returns {object}
 */
function createRootSchema(options = {}) {
    return {
        $schema: 'https://json-schema.org/draft/2020-12/schema',
        $id: options.id || 'urn:backbone:data-dictionary',
        ...(options.title ? { title: options.title } : {}),
        type: 'object',
        additionalProperties: false,
        properties: {},
        $defs: {}
    };
}

/**
 * Converts the data-dictionary map returned by extract() into a set of
 * namespace objects where every relation becomes a $ref into $defs.
 * Uses JsonSchemaRegistry for deduplication.
 * @param {DataDictionary} subDataDictionary
 * @param {JsonSchemaRegistry} registry
 * @returns {object}
 */
function buildNamespaceSchemas(subDataDictionary, registry) {
    if (!subDataDictionary) return {};

    return Object.entries(subDataDictionary).reduce((namespaces, [namespace, nsObj = {}]) => {
        const namespaceProperties = {};

        Object.entries(nsObj).forEach(([relation, relPropsMap = {}]) => {
            // Most emitters produce a full TypeBox/JSON Schema object for the relation.
            // Some nodes only declare a relation exists (e.g., foreign key references) and
            // may not contribute a complete schema; ensure we always register a valid schema
            // so each namespace.relation has a stable definition and doesn't dedupe to `undefined`.
            const isCompleteSchema =
                (relPropsMap.type && relPropsMap.properties) ||
                relPropsMap.anyOf ||
                relPropsMap.oneOf ||
                relPropsMap.allOf;

            const relSchema = isCompleteSchema
                ? relPropsMap
                : {
                      type: 'object',
                      additionalProperties: true
                  };

            // Attach x-namespace/x-relation so JsonSchemaRegistry doesn’t dedupe different relations with identical shape.
            const schemaToRegister = { ...relSchema, 'x-namespace': namespace, 'x-relation': relation };

            // Register via JsonSchemaRegistry and get $ref back
            const ref = registry.registerRelation(namespace, relation, schemaToRegister);
            namespaceProperties[relation] = ref;
        });

        namespaces[namespace] = {
            type: 'object',
            additionalProperties: false,
            properties: namespaceProperties
        };

        return namespaces;
    }, {});
}

/**
 * Some upstream merges mistakenly attach object keywords (properties/required)
 * directly to array nodes. This pass relocates those keywords into items while
 * preserving any referenced definitions the array was already pointing at.
 * @param {object} node
 */
function fixArrayObjectKeywordPlacement(node) {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
        node.forEach(fixArrayObjectKeywordPlacement);
        return;
    }
    if (node.type === 'array') {
        const objProps =
            node && typeof node.properties === 'object' && Object.keys(node.properties).length > 0
                ? node.properties
                : undefined;
        const objReq = Array.isArray(node.required) ? node.required : undefined;
        const hasAddl = Object.prototype.hasOwnProperty.call(node, 'additionalProperties');
        const objAddl = hasAddl ? node.additionalProperties : undefined;
        if (objProps || objReq || hasAddl) {
            delete node.properties;
            delete node.required;
            if (hasAddl) delete node.additionalProperties;

            if (!node.items) {
                node.items = { type: 'object' };
            }

            if (node.items.$ref) {
                const originalRef = node.items.$ref;
                const overlay = { type: 'object' };
                if (objProps) overlay.properties = objProps;
                if (objReq) overlay.required = Array.from(new Set(objReq)).sort();
                if (hasAddl) overlay.additionalProperties = objAddl;
                node.items = { allOf: [{ $ref: originalRef }, overlay] };
            } else if (node.items.allOf && Array.isArray(node.items.allOf)) {
                const overlay = { type: 'object' };
                if (objProps) overlay.properties = objProps;
                if (objReq) overlay.required = Array.from(new Set([...(overlay.required || []), ...objReq])).sort();
                if (hasAddl) overlay.additionalProperties = objAddl;
                node.items.allOf.push(overlay);
            } else {
                if (!node.items.type) node.items.type = 'object';
                if (objProps) node.items.properties = { ...(node.items.properties || {}), ...objProps };
                if (objReq)
                    node.items.required = Array.from(new Set([...(node.items.required || []), ...objReq])).sort();
                if (hasAddl) node.items.additionalProperties = objAddl;
            }
        }
    }
    if (node.properties && typeof node.properties === 'object') {
        Object.values(node.properties).forEach(fixArrayObjectKeywordPlacement);
    }
    if (node.items) fixArrayObjectKeywordPlacement(node.items);
    if (node.allOf) node.allOf.forEach(fixArrayObjectKeywordPlacement);
    if (node.anyOf) node.anyOf.forEach(fixArrayObjectKeywordPlacement);
    if (node.oneOf) node.oneOf.forEach(fixArrayObjectKeywordPlacement);
}

/**
 * Hoists identical inline object shapes into $defs to avoid bloating the
 * schema and to give OpenAPI consumers stable component names derived from the
 * metadata path.
 * @param {object} root
 * @returns {{dedupeNode:function}}
 */
function createSchemaDeduper(root) {
    const shapeMap = new Map();

    const canonicalize = obj => {
        const prune = o => {
            if (!o || typeof o !== 'object') return o;
            if (Array.isArray(o)) return o.map(prune);
            const out = {};
            Object.keys(o)
                .sort()
                .forEach(k => {
                    if (['title', 'description', 'default', 'readOnly', 'writeOnly'].includes(k)) return;
                    // Preserve x-namespace and x-relation for foreign key differentiation
                    // Skip other x-* extensions that don't affect schema identity
                    if (k.startsWith('x-') && k !== 'x-namespace' && k !== 'x-relation') return;
                    out[k] = prune(o[k]);
                });
            return out;
        };
        return JSON.stringify(prune(obj));
    };

    const sanitizeForWords = segment =>
        segment
            .toString()
            .replace(/[^A-Za-z0-9]+/g, ' ')
            .trim();

    const nextAvailableKey = keyBase => {
        if (!root.$defs[keyBase]) return keyBase;
        let i = 2;
        let candidate = `${keyBase}__${i}`;
        while (root.$defs[candidate]) {
            i++;
            candidate = `${keyBase}__${i}`;
        }
        return candidate;
    };

    const ensureShape = (schema, pathParts = []) => {
        const canon = canonicalize(schema);
        if (shapeMap.has(canon)) return shapeMap.get(canon);

        // Derive a stable, readable component name from the metadata path
        const pathSuffixCamel = camelCase(pathParts.map(sanitizeForWords).join(' '));
        const base = pathSuffixCamel || 'Shape';
        const key = nextAvailableKey(base);

        root.$defs[key] = schema;
        shapeMap.set(canon, key);
        return key;
    };

    const dedupeNode = (node, pathParts = []) => {
        if (!node || typeof node !== 'object') return;
        if (node.properties && !node.$ref) {
            Object.keys(node.properties).forEach(p => {
                const prop = node.properties[p];
                if (prop && typeof prop === 'object') {
                    // If inline object with properties (not a $ref), move to $defs
                    if (!prop.$ref && prop.type === 'object' && prop.properties) {
                        const defKey = ensureShape(prop, [...pathParts, p]);
                        node.properties[p] = { $ref: `#/$defs/${defKey}` };
                    } else if (
                        prop.type === 'array' &&
                        prop.items &&
                        prop.items.type === 'object' &&
                        prop.items.properties
                    ) {
                        const defKey = ensureShape(prop.items, [...pathParts, p, 'items']);
                        node.properties[p].items = { $ref: `#/$defs/${defKey}` };
                    } else {
                        // Recurse into nested structures to catch deeper inline object shapes
                        dedupeNode(prop, [...pathParts, p]);
                    }
                }
            });
        }
        if (node.items) dedupeNode(node.items, [...pathParts, 'items']);
        if (node.allOf) node.allOf.forEach((n, idx) => dedupeNode(n, [...pathParts, `allOf_${idx}`]));
        if (node.anyOf) node.anyOf.forEach((n, idx) => dedupeNode(n, [...pathParts, `anyOf_${idx}`]));
        if (node.oneOf) node.oneOf.forEach((n, idx) => dedupeNode(n, [...pathParts, `oneOf_${idx}`]));
    };

    return { dedupeNode };
}

/**
 * Removes `default` values from properties whose names contain dots or brackets.
 * These are nested path references (e.g., "inventory.0.found", "inventory[0].foundDate")
 * used for UI display purposes, not actual root-level schema properties. When AJV
 * validates with `useDefaults: true`, it incorrectly injects these defaults into
 * the payload at the root level. This post-processing step prevents that while
 * preserving the property definitions for UI metadata consumers.
 * @param {object} node - Schema node to process (mutates in place)
 */
function stripDefaultsFromNestedPathProperties(node) {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
        node.forEach(stripDefaultsFromNestedPathProperties);
        return;
    }

    // Check properties for dotted/bracketed names and remove their defaults
    if (node.properties && typeof node.properties === 'object') {
        Object.entries(node.properties).forEach(([propName, propSchema]) => {
            if ((propName.includes('.') || propName.includes('[')) && propSchema && propSchema.default !== undefined) {
                delete propSchema.default;
            }
            // Recurse into nested schemas
            stripDefaultsFromNestedPathProperties(propSchema);
        });
    }

    // Recurse into $defs
    if (node.$defs && typeof node.$defs === 'object') {
        Object.values(node.$defs).forEach(stripDefaultsFromNestedPathProperties);
    }

    // Recurse into other schema structures
    if (node.items) stripDefaultsFromNestedPathProperties(node.items);
    if (node.allOf) node.allOf.forEach(stripDefaultsFromNestedPathProperties);
    if (node.anyOf) node.anyOf.forEach(stripDefaultsFromNestedPathProperties);
    if (node.oneOf) node.oneOf.forEach(stripDefaultsFromNestedPathProperties);
}

module.exports = { buildSchema };
